import { Cookie } from "lucide-react";
import {
  CompliancePageHeader,
  ComplianceUnavailableState,
} from "./shared";

/**
 * D8: Cookie Consent Builder
 * WYSIWYG config for cookie consent banner.
 * Settings: banner text, button labels, categories, position, colors.
 * Stored in tenant config. Served by page-serve Worker.
 */

interface CookieConfig {
  enabled: boolean;
  bannerText: string;
  acceptLabel: string;
  rejectLabel: string;
  settingsLabel: string;
  position: "bottom" | "top" | "bottom-left" | "bottom-right";
  categories: CookieCategory[];
  primaryColor: string;
  backgroundColor: string;
}

interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
  defaultEnabled: boolean;
}

const DEFAULT_CONFIG: CookieConfig = {
  enabled: false,
  bannerText: "We use cookies to improve your experience. By continuing to visit this site you agree to our use of cookies.",
  acceptLabel: "Accept All",
  rejectLabel: "Reject All",
  settingsLabel: "Cookie Settings",
  position: "bottom",
  categories: [
    { id: "essential", name: "Essential", description: "Required for the website to function", required: true, defaultEnabled: true },
    { id: "analytics", name: "Analytics", description: "Help us understand how visitors interact", required: false, defaultEnabled: false },
    { id: "marketing", name: "Marketing", description: "Used to track visitors across websites", required: false, defaultEnabled: false },
  ],
  primaryColor: "#2563eb",
  backgroundColor: "#ffffff",
};

export default function CookieConsentBuilderPage() {
  return (
    <div className="space-y-6">
      <CompliancePageHeader
        icon={<Cookie className="h-5 w-5 text-[var(--color-primary)]" />}
        title="Cookie Consent Banner"
        description="This builder is intentionally unavailable until the page-serve configuration contract is implemented."
      />

      <ComplianceUnavailableState
        title="Cookie configuration is not backed by a live API"
        description="The old screen only mutated local component state and swallowed missing-route failures. No canonical `api-platform` or `page-serve` cookie-config handler exists yet, so this page now shows product truth instead of pretending to save."
      />

      <div className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 font-medium">Planned contract</h3>
        <div className="grid gap-2 text-sm text-muted-foreground">
          <p><span className="font-medium text-foreground">Owner:</span> `api-platform` should remain the write surface for tenant compliance settings.</p>
          <p><span className="font-medium text-foreground">Reader:</span> `page-serve` should consume the published config, not become the source of truth.</p>
          <p><span className="font-medium text-foreground">Current state:</span> no backend handler, no persisted config, no published banner contract.</p>
        </div>
      </div>
    </div>
  );
}
